import { encodePlanningCanvas } from './planning-canvas-screenshot.service';

type Extractor = () => HTMLCanvasElement | null;

export interface RenderedPlanningView {
  canvas: HTMLCanvasElement | null;
  errors: string[];
}

interface SentryErrorEvent {
  extra?: Record<string, unknown>;
}

interface SentryEventHint {
  attachments?: Array<{
    filename: string;
    data: string | Uint8Array;
    contentType?: string;
  }>;
}

const MAX_ERROR_CHARS = 500;
const MAX_ERRORS = 20;

let extractor: Extractor | null = null;
let attaching = false;

export function registerPlanningCanvasExtractor(value: Extractor): () => void {
  extractor = value;
  return () => {
    if (extractor === value) {
      extractor = null;
    }
  };
}

/**
 * Runs the stage extract and keeps the messages that render logs while producing
 * the 2D canvas. The problem-report picture is a tab capture, not this canvas.
 */
export function renderPlanningView(extract: Extractor): RenderedPlanningView {
  const errors: string[] = [];
  const previous = console.error;
  console.error = (...args: unknown[]) => {
    const text = formatError(args);
    if (text) {
      errors.push(text);
    }
    previous.apply(console, args);
  };
  try {
    return { canvas: extract() ?? null, errors: errors.slice(0, MAX_ERRORS) };
  } catch (error) {
    errors.push(formatError([error]));
    return { canvas: null, errors: errors.filter(Boolean).slice(0, MAX_ERRORS) };
  } finally {
    console.error = previous;
  }
}

/** Attaches the extracted planning view to a Sentry error, including messages from that render. */
export async function attachPlanningCanvasToError<T extends SentryErrorEvent>(
  event: T,
  hint: SentryEventHint,
): Promise<T> {
  if (attaching || !extractor) {
    return event;
  }
  attaching = true;
  try {
    const rendered = renderPlanningView(extractor);
    if (rendered.errors.length > 0) {
      event.extra = { ...(event.extra ?? {}), rendererErrors: rendered.errors };
    }
    if (!rendered.canvas) {
      return event;
    }
    const encoded = await encodePlanningCanvas(rendered.canvas);
    if (!encoded.ok) {
      return event;
    }
    const data = new Uint8Array(await readBlob(encoded.screenshot.blob));
    hint.attachments = [
      ...(hint.attachments ?? []),
      { filename: 'planning-canvas.png', data, contentType: 'image/png' },
    ];
    return event;
  } catch {
    return event;
  } finally {
    attaching = false;
  }
}

function readBlob(blob: Blob): Promise<ArrayBuffer> {
  const withBuffer = blob as Blob & { arrayBuffer?: () => Promise<ArrayBuffer> };
  if (typeof withBuffer.arrayBuffer === 'function') {
    return withBuffer.arrayBuffer();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

function formatError(args: unknown[]): string {
  return args
    .map((value) => {
      if (value instanceof Error) {
        return value.message || value.name;
      }
      if (typeof value === 'string') {
        return value;
      }
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    })
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_ERROR_CHARS);
}
